import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { isAuthenticated, sendLogin } from '../../auth/Auth';
import { getStripeChargeEvents } from '../../actions/ChargeEvent';
import { getAccounts } from '../../actions/Accounts';
import { downloadGroup } from '../../actions/Groups';
import ChargeEventTable from '../organisms/ChargeEventTable';
import SearchButton from '../../components/atoms/SearchButton';

const styles = {
  root: {
    backgroundColor: '#e0e0e0',
    flexGrow: 1,
    width: '100%'
  },
  pageTitileDiv: {
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  mainPaper: {
    position: 'relative',
    margin: 20
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  mainPaperTitleDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 26px 10px 26px'
  },
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  textField150: {
    padding: '0px 5px 0px 5px',
    width: '200px'
  },
  button: {
    margin: '5px 5px'
  },
  buttonLeft: {
    textAlign: 'left'
  },
  buttonRight: {
    textAlign: 'right'
  },
  buttonGroupLeft: {
    textAlign: 'left',
    float: 'left'
  },
  buttonGroupRight: {
    textAlign: 'right'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      statuses: state.identities.statuses
    }),
    dispatch => ({
      actions: bindActionCreators(
        { getStripeChargeEvents, getAccounts, downloadGroup },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openModal', 'updateOpenModal', false),
  withState('openEditModal', 'updateOpenEditModal', false),
  withState('openDetailModal', 'updateOpenDetailModal', false),
  withState('openDelete', 'updateOpenDelete', false),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openTable', 'updateOpenTable', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('selected', 'setSelected', []),
  withState('page', 'setPage', 1),
  withState('rowsPerPage', 'setRowsPerPage', 100),
  withState('operatorName', 'setOperatorName', 0),
  withState('gochiType', 'setGochiType', 0),
  withState('resultMessage', 'setResultMessage', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    },
    handleChangePage: props => (event, page, limit) => {
      const table = document.getElementById('scrollTableId');
      if (table !== null) {
        table.scrollTop = 0;
      }
      props.setPage(page);

      const rowsPerPage = document.getElementById('rowsPerPage');
      let limitNum = 100;
      if (rowsPerPage) {
        limitNum = rowsPerPage.textContent;
      }

      const searchKey = {
        page,
        limit: limit || limitNum
      };

      props.updateLoading(true);
      props.updateOpenProgress(true);
      props.actions
        .getStripeChargeEvents(searchKey)
        .then(data => {
          if (data.error === true && data.payload.status === 403) {
            sendLogin(props);
          }
          props.updateOpenProgress(false);
          props.updateLoading(false);
          props.updateOpenTable(true);
        })
        .catch(() => {
          props.updateOpenProgress(false);
          props.updateLoading(false);
        });
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(async data => {
        this.props.checkSession(data);

        this.props.actions
          .getStripeChargeEvents({
            page: this.props.page,
            limit: this.props.rowsPerPage
          })
          .then(stripeChargeEventData =>
            this.props.checkSession(stripeChargeEventData)
          );
        this.props.updateOpenTable(true);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    handleChangePage,
    account,
    openDrawer,
    updateOpenDrawer,
    updateOpenTable,
    openTable,
    loading,
    openProgress,
    updateLoading,
    updateOpenProgress,
    page,
    resultMessage
  }) => {
    const isShop = typeof account !== 'undefined' && account.role === 'SHOP';
    const isGroup = typeof account !== 'undefined' && account.role === 'GROUP';
    const isSecretariat =
      typeof account !== 'undefined' && account.role === 'SECRETARIAT';
    const isDeveloper =
      typeof account !== 'undefined' && account.role === 'DEVELOPER';
    const roles = {
      isShop,
      isGroup,
      isSecretariat,
      isDeveloper
    };

    const rowsPerPage = document.getElementById('rowsPerPage');
    let limitNum = 100;
    if (rowsPerPage) {
      limitNum = rowsPerPage.textContent;
    }

    const searchKey = {
      page,
      limit: limitNum
    };
    const searchAction = async () => {
      updateOpenTable(true);
      handleChangePage(null, 1, limitNum);
    };

    const progress = {
      loading,
      updateLoading,
      openProgress,
      updateOpenProgress
    };

    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            決済イベント検索
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          {(roles.isGroup || roles.isSecretariat || roles.isDeveloper) && (
            <div className={classes.searchForm}>
              <div className={classes.buttonLeft}>
                <SearchButton onClickAction={searchAction} />
              </div>
            </div>
          )}
          {openTable && (
            <ChargeEventTable
              searchKey={searchKey}
              roles={roles}
              page={page}
              handleChangePage={handleChangePage}
              selectedPage={page}
              progress={progress}
            />
          )}
        </Paper>
        <ProgressDialog
          openProgress={openProgress}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
